import { Typography, useTheme } from '@mui/joy';
import { useMemo } from 'react';
import { Trans, useTranslation } from 'react-i18next';

import { IManagedNote, INoteProps, Notes, Notification, SlidingPanel } from 'core/components';
import { NotificationPosition } from 'core/enums';
import { useNotificationContext } from 'core/states';
import { formatDate } from 'core/utils';
import { Color, Level } from 'theme/enums';

import { SubtitleWrapper } from './style';

interface IProps {
  isOpen: boolean;
  level?: number;
  onClose: () => void;
  notes: IManagedNote[];
  addButton?: JSX.Element;
  noteProps?: INoteProps;
}

export const ManageNotes = ({
  isOpen,
  level = 0,
  onClose,
  notes,
  addButton,
  noteProps = {},
}: IProps) => {
  const theme = useTheme();
  const { t } = useTranslation();

  const { clearNotification } = useNotificationContext();

  const lastUpdatedAt = useMemo(() => getLastUpdatedAt(notes), [notes]);

  const handleOnClose = () => {
    clearNotification();
    onClose();
  };

  return (
    <SlidingPanel
      title={t('titleNotes')}
      isOpen={isOpen}
      level={level}
      onClosePanel={handleOnClose}
      subtitle={
        <SubtitleWrapper>
          <Typography color={Color.NEUTRAL} level={Level.TITLE_SM}>
            <Trans
              i18nKey="subtitleNotesLastUpdate"
              values={{ value: formatDate(lastUpdatedAt, true), count: notes.length }}
              components={{
                labelWrapper: <Typography sx={{ fontWeight: 500 }} />,
                valueWrapper: (
                  <Typography sx={{ fontWeight: 600, color: theme.palette.neutral[800] }} />
                ),
              }}
            />
          </Typography>

          {addButton}
        </SubtitleWrapper>
      }
      extra={<Notification position={NotificationPosition.DRAWER} />}
    >
      {notes.length === 0 && (
        <Typography level={Level.BODY_SM} color={Color.NEUTRAL} fontStyle={'italic'}>
          {t('messageNoPartNotes')}
        </Typography>
      )}

      <Notes notes={notes} noteProps={noteProps} wrapperSx={{ padding: '24px 0 ' }} />
    </SlidingPanel>
  );
};

const getLastUpdatedAt = (notes: IManagedNote[]): null | string => {
  if (notes.length === 0) {
    return null;
  }

  const latestNote = notes.reduce((latest, current) =>
    new Date(current.updatedAt) > new Date(latest.updatedAt) ? current : latest
  );

  return latestNote.updatedAt;
};
